import * as React from 'react';
import { useMsg } from './hooks';
import LocalHTMLContainer from './LocalHTMLContainer';
import { removeRedundantBackslashes } from './removeRedundantBackslashes';
import { DefaultMessage } from './defaultMessages';
import { Map } from '../language/models';
import { doMsgReplacements } from './module';
import { useAppState } from '../core/redux/hooks';


type SpanOrDiv = Extract<HTMLDivElement, HTMLSpanElement>;
type SpanOrDivProps = React.DetailedHTMLProps<React.HTMLAttributes<SpanOrDiv>, SpanOrDiv>;


interface Props extends SpanOrDivProps {
  str: string | number;
  defaultMsg?: string | number;
  replace?: Map<string | number | undefined>;
}


/**
 * LocalMsgContainer: a component that renders a translated message as either plain text or, if it contains, markup,
 * raw HTML.
 *
 * When replacing, this component automatically adds interpolation braces to your keys
 * -- use `userName` to replace `{userName}` from the raw message text.
 *
 * Props:
 * 1. The `msgKey` prop is the key for the translated message object stored in app state.
 * 1. The `defaultMsg` prop allows the user the opportunity to pass in a default string if one is preferred over the
 * fallback string. If set to the empty string, when a translated message is missing, no component will be rendered.
 * Note that this prop is ignored if a translation exists and contains HTML markup.
 * 1. The `replace` prop is an object of `{'toReplace': 'replacement'}` key-value pairs. It's recommended that you
 * memoize this object in your component, otherwise, replacement logic will run on every render XP
 *
 * PureComponent: true
 * Connected to Redux: false
 */
const Msg: React.FC<Props> = props => {
  const { str, defaultMsg, replace, ...rest } = props;

  const msg = useMsg(props.str);

  const msgText = React.useMemo(
    () => doMsgReplacements(msg?.text ?? `${defaultMsg}`, replace),
    [msg, defaultMsg, replace],
  );

  const dir = useAppState(state => state.locale.textDirection);

  if ((msg as DefaultMessage)?.isDefault && defaultMsg === '') {
    return null;
  }

  return msg && msg.containsMarkup
    ? <LocalHTMLContainer {...rest} innerHtml={removeRedundantBackslashes(msg.text)} />
    : <span {...rest} dir={dir}>{msgText ?? defaultMsg}</span>;
};

Msg.defaultProps = {
  defaultMsg: '',
};

export default Msg;
